import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { 
  CheckSquare, 
  Check, 
  ArrowRight, 
  CheckCircle2, 
  Clock, 
  AlertCircle,
  Briefcase,
  FileText,
  Loader2 
} from "lucide-react";
import { useTasks } from "../../hooks/useTasks";
import { useAuth } from "../../context/AuthContext";
import { useUsers } from "../../hooks/useUsers";

export default function TodayTasksWidget() {
  const { tasks, loading, toggleTaskComplete } = useTasks();
  const { currentUser } = useAuth();
  const { users } = useUsers();
  const [completingId, setCompletingId] = useState(null);

  // Identify current user and alias mappings
  const currentUserObj = useMemo(() => {
    if (!currentUser) return null;
    return (users || []).find(u => 
      (u.email && currentUser.email && u.email.toLowerCase() === currentUser.email.toLowerCase()) || 
      u.id === currentUser.uid
    );
  }, [currentUser, users]);

  const currentUserName = useMemo(() => {
    if (currentUserObj?.name) return currentUserObj.name;
    if (currentUser?.displayName) return currentUser.displayName;
    if (currentUser?.email) {
      const prefix = currentUser.email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return '';
  }, [currentUser, currentUserObj]);

  const currentUserAliases = useMemo(() => {
    const aliases = new Set();
    if (currentUserObj?.name) aliases.add(currentUserObj.name.trim().toLowerCase());
    if (currentUser?.displayName) aliases.add(currentUser.displayName.trim().toLowerCase());
    if (currentUser?.email) {
      aliases.add(currentUser.email.trim().toLowerCase());
      const prefix = currentUser.email.split('@')[0].toLowerCase();
      aliases.add(prefix);
    }
    if (currentUser?.uid) aliases.add(currentUser.uid.toLowerCase());
    return Array.from(aliases).filter(Boolean);
  }, [currentUser, currentUserObj]);

  // Today's Date in local YYYY-MM-DD
  const todayStr = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }, []);

  // Filter tasks: assigned to user, NOT completed, due today or overdue
  const todayTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];

    return tasks.filter(task => {
      // 1. Only show uncompleted tasks
      if (task.completed) return false;

      // 2. Must match the logged-in user
      const assigned = (task.assignedTo || '').trim().toLowerCase();
      if (!assigned) {
        // If unassigned, do not include in private user's today's tasks
        return false;
      }
      const isAssignedToUser = currentUserAliases.includes(assigned) || 
        assigned === currentUserName.toLowerCase();
      if (!isAssignedToUser) return false;

      // 3. Must be due today or overdue
      if (!task.dueDate) {
        // If no due date, check if weekOf or createdAt matches today
        const createdDateStr = (task.createdAt || '').split('T')[0];
        return createdDateStr === todayStr;
      }

      return task.dueDate <= todayStr;
    }).sort((a, b) => {
      // Overdue tasks first, then by dueDate, then by createdAt
      if (a.dueDate !== b.dueDate) {
        return (a.dueDate || '').localeCompare(b.dueDate || '');
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
  }, [tasks, currentUserAliases, currentUserName, todayStr]);

  const handleToggleComplete = async (e, task) => {
    e.stopPropagation();
    try {
      setCompletingId(task.id);
      await toggleTaskComplete(task.id, false, currentUserName || currentUser?.email || 'User');
    } catch (err) {
      console.error("Failed to complete task:", err);
    } finally {
      setCompletingId(null);
    }
  };

  return (
    <div className="card summary-card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="card-header-flex" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <h3 className="card-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckSquare size={20} style={{ color: "var(--primary)" }} />
            Today's Tasks
          </h3>
          {!loading && (
            <span 
              style={{ 
                fontSize: '0.75rem', 
                padding: '0.15rem 0.55rem', 
                borderRadius: '12px', 
                fontWeight: 700,
                backgroundColor: todayTasks.length > 0 ? 'hsla(var(--hue-primary), 89%, 60%, 0.15)' : 'hsla(var(--hue-success), 70%, 50%, 0.15)',
                color: todayTasks.length > 0 ? 'var(--primary)' : 'var(--success)',
                border: todayTasks.length > 0 ? '1px solid hsla(var(--hue-primary), 89%, 60%, 0.3)' : '1px solid hsla(var(--hue-success), 70%, 50%, 0.3)'
              }}
            >
              {todayTasks.length} Pending
            </span>
          )}
        </div>
        <Link 
          to="/tasks" 
          className="card-link" 
          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 500 }}
        >
          View All <ArrowRight size={15} />
        </Link>
      </div>

      {/* Task Content */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            <Loader2 className="animate-spin" size={24} style={{ margin: '0 auto 0.5rem auto' }} />
            <p style={{ fontSize: '0.85rem' }}>Loading today's tasks...</p>
          </div>
        ) : todayTasks.length === 0 ? (
          <div 
            style={{ 
              padding: '1.75rem 1rem', 
              textAlign: 'center', 
              color: 'var(--text-secondary)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}
          >
            <div 
              style={{ 
                width: '48px', 
                height: '48px', 
                borderRadius: '50%', 
                backgroundColor: 'hsla(var(--hue-success), 70%, 50%, 0.12)', 
                color: 'var(--success)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '0.75rem'
              }}
            >
              <CheckCircle2 size={26} />
            </div>
            <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '0.95rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              All Caught Up for Today!
            </h4>
            <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              You have no pending tasks scheduled for today.
            </p>
            <Link 
              to="/tasks" 
              className="btn btn-secondary"
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.85rem', borderRadius: '6px' }}
            >
              Go to Tasks Board
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', paddingRight: '0.25rem' }}>
            {todayTasks.map((task) => {
              const isOverdue = task.dueDate && task.dueDate < todayStr;
              const isBusy = completingId === task.id;

              return (
                <div
                  key={task.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.75rem',
                    padding: '0.75rem 0.85rem',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-input)',
                    border: isOverdue ? '1px solid hsla(var(--hue-warning), 90%, 50%, 0.3)' : '1px solid var(--border-color)',
                    transition: 'all 0.15s ease',
                    position: 'relative'
                  }}
                  className="today-task-row"
                >
                  {/* Interactive Checkbox Toggle */}
                  <button
                    type="button"
                    onClick={(e) => handleToggleComplete(e, task)}
                    disabled={isBusy}
                    title="Mark as completed"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      cursor: isBusy ? 'wait' : 'pointer',
                      color: isBusy ? 'var(--primary)' : 'var(--text-muted)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginTop: '0.15rem',
                      flexShrink: 0
                    }}
                    aria-label={"Mark task " + task.title + " complete"}
                  >
                    {isBusy ? (
                      <Loader2 size={18} className="animate-spin" style={{ color: 'var(--primary)' }} />
                    ) : (
                      <div 
                        style={{ 
                          width: '18px', 
                          height: '18px', 
                          borderRadius: '4px', 
                          border: '2px solid var(--text-muted)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = 'var(--primary)';
                          e.currentTarget.style.backgroundColor = 'hsla(var(--hue-primary), 89%, 60%, 0.15)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'var(--text-muted)';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Check size={12} style={{ opacity: 0, transition: 'opacity 0.1s' }} />
                      </div>
                    )}
                  </button>

                  {/* Task Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div 
                      style={{ 
                        fontWeight: 600, 
                        fontSize: '0.875rem', 
                        color: 'var(--text-primary)',
                        lineHeight: 1.35,
                        wordBreak: 'break-word'
                      }}
                    >
                      {task.title || 'Untitled Task'}
                    </div>

                    {/* Metadata Badges */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.4rem', marginTop: '0.35rem' }}>
                      {/* Due Status Pill */}
                      {isOverdue ? (
                        <span 
                          style={{ 
                            fontSize: '0.7rem', 
                            padding: '0.1rem 0.4rem', 
                            borderRadius: '4px', 
                            backgroundColor: 'hsla(var(--hue-warning), 90%, 50%, 0.15)', 
                            color: 'var(--warning)', 
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.2rem'
                          }}
                        >
                          <AlertCircle size={11} /> Overdue ({task.dueDate})
                        </span>
                      ) : (
                        <span 
                          style={{ 
                            fontSize: '0.7rem', 
                            padding: '0.1rem 0.4rem', 
                            borderRadius: '4px', 
                            backgroundColor: 'hsla(var(--hue-primary), 89%, 60%, 0.12)', 
                            color: 'var(--primary)', 
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.2rem'
                          }}
                        >
                          <Clock size={11} /> Due Today
                        </span>
                      )}

                      {/* Opportunity / Record Tag */}
                      {task.oppName && (
                        <span 
                          style={{ 
                            fontSize: '0.7rem', 
                            padding: '0.1rem 0.45rem', 
                            borderRadius: '4px', 
                            backgroundColor: 'rgba(255,255,255,0.06)', 
                            color: 'var(--text-secondary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            maxWidth: '180px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title={"Opportunity: " + task.oppName}
                        >
                          <Briefcase size={10} /> {task.oppName}
                        </span>
                      )}

                      {/* Estimate Tag */}
                      {(task.estimateName || task.estimateNumber) && (
                        <span 
                          style={{ 
                            fontSize: '0.7rem', 
                            padding: '0.1rem 0.45rem', 
                            borderRadius: '4px', 
                            backgroundColor: 'rgba(255,255,255,0.06)', 
                            color: 'var(--text-secondary)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <FileText size={10} /> {task.estimateNumber || task.estimateName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
